import * as React from "react";
import { Alert, Box, FormControlLabel, Paper, Snackbar, Switch, Typography } from "@mui/material";
import { useCategoriesAdmin } from "../components/Categories/useCategoriesAdmin";
import CategoryTreeView from "../components/Categories/CategoryTreeView";
import CategoryDetailsPanel, { type FormTarget } from "../components/Categories/CategoryDetailsPanel";
import CategoryDeleteDialog from "../components/Categories/CategoryDeleteDialog";

const CategorySettingsPage: React.FC = () => {
    const {
        loading,
        error,
        clearError,
        status,
        clearStatus,
        includeSystem,
        setIncludeSystem,
        categories,
        tree,
        createCategory,
        updateCategory,
        moveCategory,
        deleteCategory,
    } = useCategoriesAdmin();

    const [target, setTarget] = React.useState<FormTarget | null>(null);
    const [deleteId, setDeleteId] = React.useState<number | null>(null);

    const selectedIdForTree = target?.mode === "edit" ? target.id : null;
    const deleteTarget = deleteId != null ? categories.find((c) => c.id === deleteId) ?? null : null;

    const handleConfirmDelete = async () => {
        if (deleteId == null) return;
        await deleteCategory(deleteId);
        if (target?.mode === "edit" && target.id === deleteId) setTarget(null);
        setDeleteId(null);
    };

    if (loading) {
        return (
            <Box sx={{ maxWidth: 1100, mx: "auto", mt: 4, textAlign: "center" }}>
                <Typography color="text.secondary">Loading categories…</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1100, mx: "auto", mt: 4, px: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 1 }}>
                <Box>
                    <Typography variant="h5" gutterBottom>
                        Categories
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Browse and edit category hierarchy. Use the
                        + buttons to add new ones.
                    </Typography>
                </Box>
                <FormControlLabel
                    sx={{ flexShrink: 0 }}
                    control={<Switch checked={includeSystem} onChange={(e) => setIncludeSystem(e.target.checked)} />}
                    label="Include system categories"
                />
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
                    {error}
                </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: "flex-start" }}>
                <Paper variant="outlined" sx={{ p: 2, width: { xs: "100%", md: 420 }, flexShrink: 0 }}>
                    <CategoryTreeView
                        tree={tree}
                        categories={categories}
                        selectedId={selectedIdForTree}
                        onSelect={(id) => setTarget({ mode: "edit", id })}
                        onAddRoot={() => setTarget({ mode: "create", parentId: null })}
                        onAddChild={(parentId) => setTarget({ mode: "create", parentId })}
                        onRequestDelete={(id) => setDeleteId(id)}
                        onMove={(id, newParentId) => {
                            // moveCategory catches its own errors and surfaces them via `error` above.
                            void moveCategory(id, newParentId);
                        }}
                    />
                </Paper>

                <Box sx={{ flexGrow: 1, width: "100%", minHeight: 320 }}>
                    <CategoryDetailsPanel
                        target={target}
                        categories={categories}
                        onCreate={createCategory}
                        onUpdate={updateCategory}
                        onRequestDelete={(id) => setDeleteId(id)}
                        onCancel={() => setTarget(null)}
                        onSaved={(id) => setTarget({ mode: "edit", id })}
                        onAddChild={(parentId) => setTarget({ mode: "create", parentId })}
                    />
                </Box>
            </Box>

            <CategoryDeleteDialog
                category={deleteTarget}
                categories={categories}
                onCancel={() => setDeleteId(null)}
                onConfirm={handleConfirmDelete}
            />

            <Snackbar
                open={!!status}
                autoHideDuration={3000}
                onClose={clearStatus}
                message={status}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            />
        </Box>
    );
};

export default CategorySettingsPage;
